/**
 * Copyright (C) Boost commerce
 * This file is part of commercial Boost commerce projects
 *
 * This file can not be copied and/or distributed without the express
 * permission of Boost commerce
 *
 * Created on Thu Mar 23 2023 11:10:55
 */
// @flow

/**
 * Format money value according to shop's money format configuration
 *
 * @param {number|string} money - The monetary value to format
 * @param {string} format - Money format string (e.g., '${{amount}}')
 * @param {boolean} withTrailingZeros - Whether to keep trailing zeros (.00)
 * @param {boolean} showCentAsSuperscript - Whether to display cents as superscript
 * @param {boolean} removePriceDecimal - Whether to remove all decimal values
 * @param {boolean} removeDecimalPoint - Whether to remove the decimal point separator
 * @param {boolean} useAbbreviation - Whether to use M, B, T, Q abbreviations for large numbers
 * @returns {string} Formatted money string
 *
 * @example
 * formatMoney(1234.56, '${{amount}}', true, false, false, false, false) // "$1,234.56"
 * formatMoney(1000000, '${{amount}}', false, false, false, false, true) // "$1M"
 * formatMoney(1500000000, '${{amount}}', false, false, false, false, true) // "$1.5B"
 */
export const formatMoney = (
  money: number | string,
  format: string,
  withTrailingZeros: boolean,
  showCentAsSuperscript: boolean,
  removePriceDecimal: boolean,
  removeDecimalPoint: boolean,
  useAbbreviation: boolean = false
) => {
  if (!['number', 'string'].includes(typeof money)) return '';
  if (!format) format = window.boostSDAppConfig?.shop?.money_format;
  if (format === 'money_with_currency')
    format = window.boostSDAppConfig?.shop?.money_format_with_currency;

  let sanitizedMoney = money;
  if (typeof money === 'string') {
    sanitizedMoney = money.replaceAll('.', '');
  }

  // Convert to number for abbreviation check
  const numericValue = parseFloat(String(sanitizedMoney));

  // Format with abbreviations for large numbers (M, B, T, Q)
  if (useAbbreviation && !isNaN(numericValue)) {
    const abbreviated = formatMoneyWithAbbreviation(numericValue);
    if (abbreviated) {
      return compileMoneyWithConfigFormat(abbreviated, format);
    }
  }

  // Get type format money
  const typeFormatMoney = getTypeFormatMoney(format);

  // Format money by type
  let moneyFormat = formatMoneyByType(typeFormatMoney, sanitizedMoney);

  // Format money with trailing zeros
  if (!withTrailingZeros) moneyFormat = moneyFormat.replace(/((,00)|(\.00))$/g, '');

  // Remove decimal
  if (removePriceDecimal) {
    moneyFormat = removeDecimal(
      moneyFormat,
      window.boostSDAppConfig?.generalSettings?.decimalDelimiter
    );
  }

  // Format money with cent as superscript
  if (showCentAsSuperscript) {
    const [ints, cents] = separateDecimalMoney(moneyFormat, typeFormatMoney, removeDecimalPoint);
    if (cents) moneyFormat = `${ints}<sup>${cents}</sup>`;
  }

  // Replace config format with money format
  return compileMoneyWithConfigFormat(moneyFormat, format);
};

export const formatMoneyAbbreviation = (num: number, moneyFormat: string) => {
  return formatMoney(num, moneyFormat, false, false, false, false, true);
}

/**
 * Format money with abbreviations for large numbers
 * @param {number} num - The numeric value to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string|null} Abbreviated money string or null if below 1M
 *
 * @example
 * formatMoneyWithAbbreviation(1000000) // "1M"
 * formatMoneyWithAbbreviation(1500000) // "1.5M"
 * formatMoneyWithAbbreviation(1000000000) // "1B"
 * formatMoneyWithAbbreviation(1500000000000) // "1.5T"
 * formatMoneyWithAbbreviation(2000000000000000) // "2Q"
 */
const formatMoneyWithAbbreviation = (num: number, decimals: number = 1) => {
  if (!num || num === 0) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  // Quadrillion
  if (absNum >= 1e15) {
    const formatted = (absNum / 1e15).toFixed(decimals);
    return sign + parseFloat(formatted) + 'Q';
  }

  // Trillion
  if (absNum >= 1e12) {
    const formatted = (absNum / 1e12).toFixed(decimals);
    return sign + parseFloat(formatted) + 'T';
  }

  // Billion
  if (absNum >= 1e9) {
    const formatted = (absNum / 1e9).toFixed(decimals);
    return sign + parseFloat(formatted) + 'B';
  }

  // Million
  if (absNum >= 1e6) {
    const formatted = (absNum / 1e6).toFixed(decimals);
    return sign + parseFloat(formatted) + 'M';
  }

  // Thousand
  if (absNum >= 1e4) {
    const formatted = (absNum / 1e3).toFixed(decimals);
    return sign + parseFloat(formatted) + 'K';
  }

  // Below million, return null to use standard formatting
  return null;
};

const stripHtml = (string: string) => {
  return string?.replace(/<[^>]*>?/gi, '').replaceAll('&#36;', '$');
};

const compileMoneyWithConfigFormat = (moneyFormat: string, format: string) => {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  // eslint-disable-next-line no-template-curly-in-string
  let formatString = format || '${{amount}}';

  formatString = stripHtml(formatString).replace(placeholderRegex, moneyFormat);

  return formatString;
};

const getDecimalPoint = (format: string) => {
  switch (format) {
    case 'amount':
    case 'amount_with_space_separator_no_comma':
      return '.';
    case 'amount_with_comma_separator':
      return ',';
    case 'amount_no_decimals':
    case 'amount_no_decimals_with_comma_separator':
    case 'amount_no_decimals_with_space_separator':
    default:
      return null;
  }
};

const removeDecimal = (value: string | number, delimiter: string) => {
  const reg = new RegExp('(\\' + delimiter + '\\d+)+', 'gi');
  return ('' + value).replace(reg, '');
};

const separateDecimalMoney = (
  moneyFormat: string | number,
  format: string,
  removeDecimalPoint: boolean
) => {
  const decimalPoint = getDecimalPoint(format);
  if (!decimalPoint) return [`${moneyFormat}`];

  const [ints, cents] = `${moneyFormat}`.split(decimalPoint);

  return cents ? [ints, removeDecimalPoint ? `${cents}` : `${decimalPoint}${cents}`] : [ints];
};

export const formatMoneyByType = (type: string, money: number | string) => {
  function defaultOption(opt: number | string, def: number | string) {
    return typeof opt == 'undefined' ? def : opt;
  }

  function formatWithDelimiters(
    number: number | string | null,
    precision: number,
    thousands: string,
    decimal: string
  ) {
    if (number !== 0 && !number) return '';

    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    number = parseFloat(`${number}`).toFixed(precision);

    const parts = number.toString().split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const money = parts[1] ? decimal + parts[1] : '';

    return dollars + money;
  }

  switch (type) {
    case 'amount':
      return formatWithDelimiters(money, 2);
    case 'amount_no_decimals':
      return formatWithDelimiters(money, 0);
    case 'amount_with_comma_separator':
      return formatWithDelimiters(money, 2, '.', ',');
    case 'amount_with_dot_separator':
      return formatWithDelimiters(money, 2, ',', '.');
    case 'amount_no_decimals_with_comma_separator':
      return formatWithDelimiters(money, 0, '.', ',');
    case 'amount_with_space_separator_no_comma':
      return formatWithDelimiters(money, 2);
    case 'amount_no_decimals_with_space_separator':
      return formatWithDelimiters(money, 0, ' ', '.');
    default:
      return formatWithDelimiters(money, 2);
  }
};

const getTypeFormatMoney = (format: string) => {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  // eslint-disable-next-line no-template-curly-in-string
  const formatString = format || '${{amount}}';
  const matches = formatString.match(placeholderRegex);
  const match = matches && matches.length > 1 ? matches[1] : '';
  return match;
};
